import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SmartContentCard from './SmartContentCard';

/**
 * Chronological grid layout - left to right (newest to oldest), top to bottom
 * @param {Array} items - Array of DeepSearch result items (should already be sorted newest first)
 * @param {Object} searchMeta - Search metadata containing matchReasons for items
 */
export default function MasonryGrid({ items = [], onItemClick, onItemDelete, isBulkSelectMode = false, selectedItems = new Set(), onBulkSelect = null, searchMeta = null }) {
  // Ensure items are sorted by date (newest first) for left-to-right display
  const sortedItems = useMemo(() => {
    if (!items || items.length === 0) return [];
    
    return [...items].sort((a, b) => {
      const dateA = new Date(a.created_at || a.createdAt || 0);
      const dateB = new Date(b.created_at || b.createdAt || 0);
      return dateB - dateA; // Newest first (leftmost)
    });
  }, [items]);

  if (!sortedItems || sortedItems.length === 0) {
    return (
      <div className="text-center py-12 text-neutral-500">
        <p className="text-lg mb-2">No items to display.</p>
        <p className="text-sm">Try adjusting your search or filters.</p>
      </div>
    );
  }

  // Build match reasons from search metadata
  const getMatchReasons = (itemId) => {
    if (!searchMeta) return null;
    
    const reasons = [];
    
    // Color match reason
    if (searchMeta.colorQuery && searchMeta.colorQuery.length > 0) {
      const colorText = searchMeta.colorQuery.join(', ');
      reasons.push({ type: 'color', text: `Color: ${colorText}` });
    }
    
    // Search mode reason
    if (searchMeta.fallbackLevel) {
      if (searchMeta.fallbackLevel === 5) {
        reasons.push({ type: 'color-tag', text: 'Matched by color tag' });
      }
    }
    
    // Similar item reason
    if (searchMeta.sourceItemTitle) {
      reasons.push({ type: 'similar', text: `Similar to: ${searchMeta.sourceItemTitle}` });
    }
    
    return reasons.length > 0 ? reasons.slice(0, 2) : null;
  };

  return (
    <div 
      className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 sm:gap-5 lg:gap-6 relative z-20"
      style={{ 
        pointerEvents: 'auto',
        touchAction: 'pan-y'
      }}
    >
      <AnimatePresence mode="popLayout" initial={false}>
        {sortedItems.map((item) => (
          <motion.div
            key={item.id}
            layout={false}
            initial={false}
            animate={{ opacity: 1 }}
            exit={{ 
              opacity: 0,
              transition: { duration: 0.15, ease: 'easeOut' }
            }}
            transition={{ duration: 0 }}
            className="flex"
            style={{ pointerEvents: 'auto', willChange: 'auto' }}
          >
            <SmartContentCard 
              item={item} 
              onClick={onItemClick} 
              onDelete={onItemDelete}
              isBulkSelectMode={isBulkSelectMode}
              isSelected={selectedItems.has(item.id)}
              onBulkSelect={onBulkSelect}
              matchReasons={getMatchReasons(item.id)}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

